import { useRouter } from 'next/router';

import { useIsClient } from '@/hooks/ui/useIsClient';

import type { NextRouter } from 'next/router';

interface SafeRouter {
  asPath: string;
  back: NextRouter['back'];
  isReady: boolean;
  pathname: string;
  push: NextRouter['push'];
  query: NextRouter['query'];
  reload: NextRouter['reload'];
  replace: NextRouter['replace'];
}

/**
 * A safe wrapper around Next.js useRouter that prevents SSR errors
 * by providing fallback values when the router isn't ready.
 *
 * @returns SafeRouter object with SSR-safe properties and methods
 *
 * @example
 * ```typescript
 * const { pathname, asPath, query, isReady } = useRouterSafe();
 *
 * // Safe to use during SSR - will return '/' as fallback
 * const isHomePage = pathname === '/';
 *
 * // Only access query when ready
 * useEffect(() => {
 *   if (isReady) {
 *     console.log('Current search:', query.search);
 *   }
 * }, [isReady, query.search]);
 * ```
 */
export const useRouterSafe = (): SafeRouter => {
  const router = useRouter();
  const isClient = useIsClient();

  return {
    // Safe string properties with fallbacks
    pathname: router.isReady ? router.pathname : '/',
    asPath: isClient && router.isReady ? router.asPath : '/',
    query: router.isReady ? router.query : {},

    // Router state
    isReady: router.isReady,

    // Navigation methods (these are safe to call anytime)
    push: router.push,
    replace: router.replace,
    back: router.back,
    reload: router.reload,
  };
};
